import { useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useFirebaseStorageUrl } from "@/context/FirebaseStorageUrlContext";

interface FirebaseImageProps {
  firebaseUrl: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  borderRadius?: string | number;
  fallbackText?: string;
  onLoad?: (hasError: boolean) => void;
}

export const FirebaseImage = ({
  firebaseUrl,
  alt = "Image",
  width = "100%",
  height = "100%",
  objectFit = "cover",
  borderRadius = 0,
  fallbackText = "Image",
  onLoad,
}: FirebaseImageProps) => {
  const theme = useTheme();
  const { url, loading, error } = useFirebaseStorageUrl(firebaseUrl);

  useEffect(() => {
    if (url) {
      onLoad?.(false);
    }
  }, [url, onLoad]);

  if (loading) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius,
          bgcolor: theme.palette.grey[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error || !url) {
    return (
      <Box
        sx={{
          width,
          height,
          borderRadius,
          bgcolor: theme.palette.grey[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {fallbackText}
        </Typography>
      </Box>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      style={{
        width,
        height,
        objectFit,
        borderRadius:
          typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
      }}
    />
  );
};
